import { useMemo, useState } from 'react';
import { Container, Row, Col } from "react-bootstrap";
import { ColDef, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { useLoaderData, useNavigate } from "react-router-dom";
import { ApplyPayment, Payment } from "./Data/ApiTypes";
import { cents, showMoney } from "./Data/Money";
import { showPaymentType } from "./Data/Finance";
import { gridOneCol, useParamIds } from "./Data/Common";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { Plus } from "react-bootstrap-icons";
import { colInstantDateTime, DataGrid } from "./Controls/DataGrid";
import { RecordPaymentFormModal } from "./FacilityParkerPaymentListPageRecordPaymentModal";
import { AddCreditFormModal } from "./FacilityParkerPaymentListPageAddCreditModal";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { toSnapshot } from "dinero.js";
import { useParkerBalance } from "./hooks/useParkerBalance";

export function FacilityParkerPaymentListPage() {
    const nav = useNavigate();
    const { facilityId, parkerId } = useParamIds();
    const loadedPayments = useLoaderData() as Payment[];
    const { balance, setBalance, applyCredit } = useParkerBalance();

    const [ showRecordPayment, setShowRecordPayment ] = useState( false );
    const [ showAddCredit,     setShowAddCredit     ] = useState( false );
    const [ payments,          setPayments          ] = useState( loadedPayments );

    const desc = ( src: ValueGetterParams<Payment, ApplyPayment[]> ): string => {
        const apps = src.data!.applications;
        if( apps.length === 0 ) { return "Payment Not Applied";       }
        if( apps.length === 1 ) { return `Order #${apps[0].orderId}`; }
        const orders = [...new Set( apps.map( a => a.orderId ) )].map( orderId => `#${orderId}` ).join( ", " );
        return `Orders ${orders}`;
    }

    const paymentColumns = useMemo<ColDef<Payment>[]>( () => [
        { field: 'paymentId',    headerName: 'Payment #', type: 'rightAligned' },
        { ...colInstantDateTime( "paidWhen", x => x.facility.timeZone ), headerName: "Payment Date" },
        {
        field: 'type',
        headerName: 'Type',
        filter: 'agTextColumnFilter', 
        valueFormatter: x => showPaymentType( x.value, x.data?.staffName ),
        filterValueGetter: x => showPaymentType( x.data!.type, x.data!.staffName ),
        getQuickFilterText: x => showPaymentType( x.data!.type, x.data!.staffName ),
        },
        {                        headerName: "Orders Affected",              valueGetter: desc },
        { field: 'amount',       headerName: 'Amount',    type: 'rightAligned', valueFormatter: x => showMoney( x.value ) },
        { field: 'status',       headerName: 'Status',    type: 'rightAligned' },
        { field: 'remaining',    headerName: 'Adjusted', type: 'rightAligned', valueFormatter: x => showMoney( x.value ) },
        { field: 'unapplied',    headerName: 'Unapplied', type: 'rightAligned', valueFormatter: x => showMoney( x.value ) },
    ], [] );

    const onGridReady = ( params: GridReadyEvent ) => {
        params.api.sizeColumnsToFit();
    };

    const onRowClicked = ( event: any ) => {
        const pmt: Payment = event.data;
        nav( `/facility/${pmt.facilityId}/parker/${parkerId}/payment/${pmt.paymentId}` );
    };

    const applyPayment = ( val: Payment ) => {
        setPayments( prev => [...prev, val] );
        applyCredit( val );
    };

    return <Container fluid className="h-100">
        <RecordPaymentFormModal
            show={showRecordPayment}
            setShow={setShowRecordPayment}
            facilityId={facilityId}
            parkerId={parkerId}
            onSave={result => {
                if ( result.kind === "check" ) {
                    Api.paymentCheckCreate( facilityId, parkerId, result.data ).then( res =>
                        processResult( res, applyPayment ),
                        err => console.log( err )
                    );
                } else {
                    Api.paymentImportCreate( facilityId, parkerId, result.data ).then( res =>
                        processResult( res, applyPayment ),
                        err => console.log( err )
                    );
                }
            }}
        />

        <AddCreditFormModal
            show={showAddCredit}
            setShow={setShowAddCredit}
            facilityId={facilityId}
            parkerId={parkerId}
            onSave={data => {
                Api.paymentCreditCreate(facilityId, parkerId, data).then(res =>
                    processResult(res, val => {
                        setPayments(prev => [...prev, val]);
                        applyCredit(val);
                    }),
                    err => console.log(err)
                );
            }}
        />

        <Row className="h-100">
            <Col className="h-100" style={gridOneCol( "max-content 1fr 5px" )}>
                <PageBar title="Payments">
                    <ToolButton onClick={ () => setShowRecordPayment( true ) }>
                        <Plus size={18} /> Record Payment
                    </ToolButton>
                    <ToolButton onClick={ () => setShowAddCredit( true ) }>
                        <Plus size={18} color="magenta" /> Add Credit
                    </ToolButton>
                </PageBar>
                <div style={{ height: "100%", width: '100%' }}>
                    <DataGrid
                        onRowClicked={onRowClicked}
                        rowData={payments}
                        columnDefs={paymentColumns}
                        onGridReady={onGridReady}
                        domLayout="autoHeight"
                    />
                </div>
            </Col>
        </Row>
    </Container>;
}
